import React, { CSSProperties, Component, } from "react";
import { localize } from "src/l10n";
import { DraftHandleValue, EditorState, Modifier } from "draft-js";
import { Label, UnstyledButton } from "src/ui";
import createMentionPlugin, { 
    MentionData } from "@draft-js-plugins/mention";
import createEmojiPlugin, { defaultTheme as defaultEmojiTheme, EmojiPlugin } from "@draft-js-plugins/emoji";
import Editor from "@draft-js-plugins/editor";
import { Subject, Subscription } from "rxjs";
import { ISearchParams, searchUsers } from "src/search";
import { searchGroupMembers } from "src/groups/groups-api";
import { debounceTime } from "rxjs/operators";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { ComposerStage, defaultFrameColors, defaultFrameFonts } from "./Visage2Composer";
import { SpintrTypes } from "src/typings";
import { ProductPlugin, createProductPlugin, ProductSuggestionsProps } from "./plugins";
import { getAddressBarPastedUrl } from "src/utils/getAddressBarPastedUrl";
import Visage2ComposerEmojiSelect from "./Visage2ComposerEmojiSelect";

interface IProps {
    disableEffects?: boolean | undefined;
    editorState: EditorState;
    post: Spintr.ISocialPostBase;
    setStep: any;
    onPostUpdate: any;
    onEditorStateUpdate: (editorState: EditorState) => void;
    placeholder?: string;
    onProductAdded?:    ProductSuggestionsProps["onAddProduct"] | undefined;
    onProductRemoved?:  ProductSuggestionsProps["onRemoveProduct"] | undefined;
    productsOptions?: {
        enabled: boolean;
        patterns: RegExp[];
    };
    mentionSearchParams?: Partial<ISearchParams>;
}

interface IState {
    suggestions: MentionData[];
    mentionOpen: boolean;
}

class Visage2ComposerTextInput extends Component<IProps, IState> {
    protected mentionPlugin: ReturnType<typeof createMentionPlugin>;
    protected emojiPlugin: EmojiPlugin;
    protected productPlugin: ProductPlugin;
    protected mentionSubject: Subject<string>;
    protected mentionSubscription: Subscription;
    protected editorRef: React.RefObject<Editor>;

    constructor(props: IProps) {
        super(props);
        
        this.editorRef = React.createRef<Editor>();
        this.mentionPlugin = createMentionPlugin({
            supportWhitespace: true 
        });
        this.emojiPlugin = createEmojiPlugin({
            // The picker's facelift look on top of the plugin's own (AMR-414)
            theme: {
                ...defaultEmojiTheme,
                emojiSelect: defaultEmojiTheme.emojiSelect + " composer-emoji-root",
                emojiSelectButton: "composer-emoji-button",
                emojiSelectButtonPressed: "composer-emoji-button is-open",
                emojiSelectPopover: defaultEmojiTheme.emojiSelectPopover + " composer-emoji-popover",
                emojiSelectPopoverTitle: defaultEmojiTheme.emojiSelectPopoverTitle + " composer-emoji-title",
                emojiSelectPopoverGroups: defaultEmojiTheme.emojiSelectPopoverGroups + " composer-emoji-groups",
                emojiSelectPopoverGroup: defaultEmojiTheme.emojiSelectPopoverGroup + " composer-emoji-group",
                emojiSelectPopoverGroupTitle: defaultEmojiTheme.emojiSelectPopoverGroupTitle + " composer-emoji-groupTitle",
                emojiSelectPopoverGroupList: defaultEmojiTheme.emojiSelectPopoverGroupList + " composer-emoji-list",
                emojiSelectPopoverGroupItem: defaultEmojiTheme.emojiSelectPopoverGroupItem + " composer-emoji-item",
                emojiSelectPopoverEntry: defaultEmojiTheme.emojiSelectPopoverEntry + " composer-emoji-entry",
                emojiSelectPopoverEntryFocused: defaultEmojiTheme.emojiSelectPopoverEntryFocused + " composer-emoji-entry is-focused",
                emojiSelectPopoverToneSelect: defaultEmojiTheme.emojiSelectPopoverToneSelect + " composer-emoji-toneSelect",
                emojiSelectPopoverNav: defaultEmojiTheme.emojiSelectPopoverNav + " composer-emoji-nav",
                emojiSelectPopoverNavItem: defaultEmojiTheme.emojiSelectPopoverNavItem + " composer-emoji-navItem",
                emojiSelectPopoverNavEntry: defaultEmojiTheme.emojiSelectPopoverNavEntry + " composer-emoji-navEntry",
                emojiSelectPopoverNavEntryActive: defaultEmojiTheme.emojiSelectPopoverNavEntryActive + " composer-emoji-navEntry is-active",
            },
            selectButtonContent: <Visage2Icon icon="emoji-happy" size="small" color="grey" />,
            selectGroups: [
                { title: localize("EmojiManniskor"), icon: <Visage2Icon icon="emoji-happy" size="small" />, categories: ["people"] },
                { title: localize("EmojiNatur"), icon: <Visage2Icon icon="tree" size="small" />, categories: ["nature"] },
                { title: localize("EmojiMatOchDryck"), icon: <Visage2Icon icon="coffee" size="small" />, categories: ["food"] },
                { title: localize("EmojiAktiviteter"), icon: <Visage2Icon icon="medal-star" size="small" />, categories: ["activity"] },
                { title: localize("EmojiResorOchPlatser"), icon: <Visage2Icon icon="airplane" size="small" />, categories: ["travel"] },
                { title: localize("EmojiForemal"), icon: <Visage2Icon icon="lamp-on" size="small" />, categories: ["objects"] },
                { title: localize("EmojiSymboler"), icon: <Visage2Icon icon="heart" size="small" />, categories: ["symbols"] },
                { title: localize("EmojiFlaggor"), icon: <Visage2Icon icon="flag" size="small" />, categories: ["flags"] },
            ],
            // Under the button, right-aligned with it; fixed, as the composer's body scrolls and clips
            selectPopperOptions: {
                strategy: "fixed",
                placement: "bottom-end",
                modifiers: [{ name: "offset", options: { offset: [0, 4] } }],
            },
        });
        this.productPlugin = createProductPlugin({
            enabled: props.productsOptions?.enabled || false,
            patterns: props.productsOptions?.patterns || [],
            items: props.post?.content
                ?.filter((c) => c.Type === SpintrTypes.UberType.Product)
                ?.map((p) => p as Spintr.ISocialProduct)
                ?.map((p) => ({
                    id: p.Id,
                    name: p.name,
                    description: "",
                    articleId: p.articleId,
                    imageUrl: p.productImageUrl,
                    created: "",
                    lastModified: "",
                    status: SpintrTypes.ContentStatus.Published,
                    metadata: [],
                })),
        });

        this.mentionSubject = new Subject();
        this.mentionSubscription = this.mentionSubject
            .pipe(debounceTime(100))
            .subscribe(this.onMentionValue.bind(this));

        this.state = {
            suggestions: [],
            mentionOpen: false,
        }
    }
    
    public componentDidMount(): void {
        if (!this.editorRef.current) {
            return;
        }

        requestAnimationFrame(() => {
            this.editorRef.current.focus();
        });
    }

    public componentWillUnmount(): void {
        this.mentionSubscription.unsubscribe();
    }

    protected onAddMention(_: MentionData) {
        /* ignore */
    }

    protected async onMentionValue(value: string): Promise<void> {
        try {
            const query = value.trim();

            if (!query) {
                this.setState({ suggestions: [] });

                return;
            }

            let suggestions: MentionData[] = [];

            if (
                this.props.mentionSearchParams?.onlyGroupResults &&
                this.props.mentionSearchParams?.groupId !== undefined &&
                this.props.mentionSearchParams?.groupId !== null
            ) {
                const members = await searchGroupMembers({
                    groupId: this.props.mentionSearchParams.groupId,
                    searchText: query,
                    take: this.props.mentionSearchParams?.take,
                    skip: this.props.mentionSearchParams?.skip,
                });

                suggestions = members.map(member => ({
                    avatar: member.image,
                    key: member.id,
                    link: `/goto/1/${member.id}`,
                    name: member.name,
                }));
            } else {
                const params: ISearchParams = {
                    ...this.props.mentionSearchParams,
                    query,
                };

                const results = await searchUsers(params);

                suggestions = results.data.map(hit => ({
                    avatar: hit.imageUrl,
                    key: hit.id,
                    link: `/goto/1/${hit.id}`,
                    name: hit.name
                }));
            }

            this.setState({ suggestions });
        } catch (err) {
            console.log(err);
        }
    }

    protected onSearchChange(event: { value: string }): void {
        const { value } = event;

        if (value) {
            this.mentionSubject.next(value);
            return;
        }

        this.setState(
            { suggestions: [] },
            () => this.mentionSubject.next(value)
        );
    }

    protected handlePastedText = (text: string, html?: string): DraftHandleValue => {
        if (this.props.post.StatusType === SpintrTypes.StatusType.Frame) {
            const currentContent = this.props.editorState.getCurrentContent();
            const currentContentLength = currentContent.getPlainText('').length;
            const maxLength = 180;

            const remainingLength = maxLength - currentContentLength;

            if (remainingLength <= 0) {
                return 'handled';
            }

            const trimmedText = text.slice(0, remainingLength);

            this.props.onEditorStateUpdate(EditorState.push(
                this.props.editorState,
                Modifier.replaceText(
                    this.props.editorState.getCurrentContent(),
                    this.props.editorState.getSelection(),
                    trimmedText.replace(/\n/g, ' ')
                ),
                'insert-characters'
            ));

            return 'handled';
        }

        const addressBarUrl = getAddressBarPastedUrl(text, html);
        if (addressBarUrl) {
            this.props.onEditorStateUpdate(EditorState.push(
                this.props.editorState,
                Modifier.replaceText(
                    this.props.editorState.getCurrentContent(),
                    this.props.editorState.getSelection(),
                    addressBarUrl
                ),
                'insert-characters'
            ));

            return 'handled';
        }

        return 'not-handled';
    }

    protected renderEditor() {
        const { MentionSuggestions } = this.mentionPlugin;
        const { EmojiSuggestions } = this.emojiPlugin;
        const { ProductSuggestions } = this.productPlugin;

        return (
            <div>
                <ProductSuggestions
                    onAddProduct={this.props.onProductAdded}
                    onRemoveProduct={this.props.onProductRemoved}
                />
                <Editor
                    ref={this.editorRef}
                    editorState={this.props.editorState}
                    placeholder={this.props.placeholder}
                    textAlignment={this.props.post.StatusType === SpintrTypes.StatusType.Frame ? "center" : "left"}
                    onChange={(editorState: EditorState) => {
                        this.props.onEditorStateUpdate(editorState);
                    }}
                    handleBeforeInput={() => {
                        if (this.props.post.StatusType === SpintrTypes.StatusType.Frame) {
                            const currentContent = this.props.editorState.getCurrentContent();
                            const currentContentLength = currentContent.getPlainText('').length;

                            if (currentContentLength > 180) {
                                return 'handled';
                            }
                        }
                    }}
                    handlePastedText={this.handlePastedText}
                    plugins={[
                        this.mentionPlugin,
                        this.emojiPlugin,
                        this.productPlugin,
                    ]}
                />
                    <MentionSuggestions
                        onOpenChange={this.onMentionOpenChanged}
                        open={this.state.mentionOpen}
                        onAddMention={this.onAddMention.bind(this)}
                        suggestions={this.state.suggestions}
                        onSearchChange={this.onSearchChange.bind(this)}
                    />
                <EmojiSuggestions />
            </div>
        )
    }

    // A recent emoji from the picker, inserted as the plugin inserts its own: where the cursor
    // was, replacing a selection, with a space after it at the end of the line
    protected insertEmoji = (unicode: string) => {
        const { editorState } = this.props;
        const content = editorState.getCurrentContent().createEntity("emoji", "IMMUTABLE", { emojiUnicode: unicode });
        const entityKey = content.getLastCreatedEntityKey();
        const selection = editorState.getSelection();
        let withEmoji = Modifier.replaceText(content, selection, unicode, undefined, entityKey);
        const end = withEmoji.getSelectionAfter();

        if (end.getAnchorOffset() === withEmoji.getBlockForKey(end.getAnchorKey()).getLength()) {
            withEmoji = Modifier.insertText(withEmoji, end, " ");
        }

        const pushed = EditorState.push(editorState, withEmoji, "insert-characters");

        this.props.onEditorStateUpdate(EditorState.forceSelection(pushed, withEmoji.getSelectionAfter()));
    };

    protected renderEmojiSelect() {
        return (
            <Visage2ComposerEmojiSelect
                emojiPlugin={this.emojiPlugin}
                onInsertEmoji={this.insertEmoji}
                onClosed={() => {
                    setTimeout(() => this.editorRef.current?.focus(), 0);
                }} />
        );
    }

    protected onMentionOpenChanged = (open: boolean) => {
        this.setState({ mentionOpen: open });
    }

    public render() {
        if (this.props.post.StatusType === SpintrTypes.StatusType.Frame) {
            const style: CSSProperties = {
                background: `linear-gradient(${this.props.post.color.from}, ${this.props.post.color.to})`,
                fontFamily: defaultFrameFonts[this.props.post.font - 1],
                fontWeight: 600
            };

            return (
                <div className="frame-form" style={style}>
                    <div className="frame-form-inner">
                    {this.renderEditor()}
                    </div>
                    <UnstyledButton
                        className="absolute-button text"
                        onClick={() => {
                            this.props.setStep(ComposerStage.SelectFrameFont);
                        }}
                    >
                        <Visage2Icon icon="text" />
                    </UnstyledButton>
                    <UnstyledButton
                        className="absolute-button remove-button close-button-circle"
                        onClick={() => {
                            this.props.onPostUpdate({
                                ...this.props.post,
                                StatusType: SpintrTypes.StatusType.Text,
                                background: "",
                                emoji: undefined,
                                font: undefined,
                                color: undefined
                            });
                        }}
                    >
                        <Visage2Icon icon="close-circle" />
                    </UnstyledButton>
                    <div className="absolute-button emoji">
                        {this.renderEmojiSelect()}
                    </div>
                </div>
            )
        }

        return (
            <div className="composer-form">
                {this.renderEditor()}
                <div className="composer-form-tools">
                    {this.getStatusType() === SpintrTypes.StatusType.Text && !this.props.disableEffects && (
                        <div className="effect-button">
                            <UnstyledButton
                                ariaLabel={localize("ADD_EFFECT")}
                                title={localize("ADD_EFFECT")}
                                onClick={() => {
                                    const c = defaultFrameColors[0];

                                    this.props.onPostUpdate({
                                        ...this.props.post,
                                        StatusType: SpintrTypes.StatusType.Frame,
                                        emoji: "",
                                        font: 1,
                                        color: c,
                                        background: {
                                            start: {
                                                color: c.from
                                            },
                                            stop: {
                                                color: c.to
                                            }
                                        }
                                    });
                                }}
                            >
                                <Label as="div" size="body-2" weight="medium">
                                    Aa
                                </Label>
                            </UnstyledButton>
                        </div>
                    )}
                    {this.renderEmojiSelect()}
                </div>
            </div>
        )
    }

    getStatusType() {
        if (this.props.post.content.find(x => x.StatusType === SpintrTypes.StatusType.BusinessDeal)) {
            return SpintrTypes.StatusType.BusinessDeal;
        } else if (this.props.post.content.find(x => x.StatusType === SpintrTypes.StatusType.SomethingFun)) {
            return SpintrTypes.StatusType.SomethingFun;
        } else if (this.props.post.content.find(x => x.StatusType === SpintrTypes.StatusType.Kudos)) {
            return SpintrTypes.StatusType.Kudos;
        } else if (this.props.post.content.find(x => x.StatusType === SpintrTypes.StatusType.SystemStatus)) {
            return SpintrTypes.StatusType.SystemStatus
        } else {
            return this.props.post.StatusType;
        }
    }
}

export default Visage2ComposerTextInput;
